import { FC, MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import { SCUIImagePreviewModal } from '../SCUIImagePreviewModal/SCUIImagePreviewModal';

export interface SCUIZoomableImageProps {
    src: string;
    previewSrc?: string;
    alt?: string;
    disabled?: boolean;
    imageSx?: SxProps<Theme>;
    containerSx?: SxProps<Theme>;
    className?: string;
    /** Parent-controlled preview; when set, built-in modal is not rendered. */
    onPreview?: (url: string) => void;
    onLoad?: () => void;
    onError?: () => void;
    children?: ReactNode;
}

export const SCUIZoomableImage: FC<SCUIZoomableImageProps> = ({
    src,
    previewSrc,
    alt = '',
    disabled = false,
    imageSx,
    containerSx,
    className,
    onPreview,
    onLoad,
    onError,
    children,
}) => {
    const { t } = useTranslation();
    const [internalPreviewUrl, setInternalPreviewUrl] = useState<string | null>(null);

    const openPreview = (event: MouseEvent) => {
        event.stopPropagation();
        if (disabled || !src) {
            return;
        }

        const url = previewSrc || src;
        if (!url) {
            return;
        }

        if (onPreview) {
            onPreview(url);
            return;
        }

        setInternalPreviewUrl(url);
    };

    return (
        <>
            <Box
                className={className}
                onClick={openPreview}
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: disabled || !src ? 'default' : 'zoom-in',
                    ...containerSx,
                    '&:hover .scui-zoomable-image__magnify-button': {
                        opacity: disabled || !src ? 0 : 1,
                    },
                }}
            >
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    draggable={false}
                    onLoad={onLoad}
                    onError={onError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        ...imageSx,
                    }}
                />
                <Tooltip title={t('studio.clickToEnlarge')} arrow placement="top">
                    <IconButton
                        className="scui-zoomable-image__magnify-button"
                        aria-label={t('studio.clickToEnlarge')}
                        onClick={openPreview}
                        disabled={disabled || !src}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 3,
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                        }}
                    >
                        <ZoomInIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {children}
            </Box>
            {!onPreview && (
                <SCUIImagePreviewModal imageUrl={internalPreviewUrl} onClose={() => setInternalPreviewUrl(null)} />
            )}
        </>
    );
};
